import React, { useState, useEffect } from "react";
import axios from "axios";
import "./activations.scss";
import countries from "./countries.js";
import { Link, useNavigate } from "react-router-dom";
import dateFormat from '../../util/dateFormat.js'
import bg from "../../img/bgg.jpg";

const Activations = () => {
  const navigate = useNavigate();
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [ip, setIp] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showError, setShowError] = useState(false);
  const [destination, setDestination] = useState("");
  const [location, setLocation] = useState({ lat: null, lng: null });

  const [eventName, setEventName] = useState("");
  const [eventLocked, setEventLocked] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [error, setError] = useState("");


  const date = new Date()

  /* =========================
     Load Event from storage
     ========================= */
  useEffect(() => {
    const savedEvent = localStorage.getItem("eventName");
    if (savedEvent) {
      setEventName(savedEvent);
      setEventLocked(true);
    }
  }, []);

  /* =========================
     Get IP & Location
     ========================= */
  useEffect(() => {
    // Fetch user IP and approximate location
    axios
      .get("https://ipinfo.io/json?token=6bebd53a7455c1", {
        withCredentials: false,
      }) // Replace with your token from ipinfo.io
      .then((response) => {
        const userIp = response.data.ip;
        setIp(userIp);

        // Extract latitude and longitude
        const [lat, lng] = response.data.loc.split(",");
        setLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
      })
      .catch((error) => console.error("IP-based location error:", error));
  }, []);

  /* =========================
     Email Validation
     ========================= */
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);

    if (val && !isValidEmail(val)) {
      setError("Invalid email format");
    } else {
      setError("");
    }
  };

  /* =========================
     Save Event (manual)
     ========================= */
  const saveEvent = () => {
    if (!eventName.trim()) {
      setError("Event name is required");
      return;
    }
    localStorage.setItem("eventName", eventName.trim());
    setEventLocked(true);
  };

  /* =========================
     Reset Event
     ========================= */
  const resetEvent = () => {
    localStorage.removeItem("eventName");
    setEventName("");
    setEventLocked(false);
    setName("");
    setEmail("");
    setContact("");
  };

  /* =========================
     Submit Form
     ========================= */
  const submitForm = async (e) => {
    e.preventDefault();
    setError("");
    setResponse("");

    // Validate form
    if (!eventLocked || !name || !email ) {
      setError("Please complete all required fields.");
      return;
    }

    const data = {
      eventName,
      name,
      email,
      contact,
      date,
      ip,
      location,
      nationality: destination,
      eventDate: dateFormat(date),
    };

    try {
      setLoading(true);

      // Post to backend
      const response = await axios.post(`/event-create-survey`, data);

      // Show success message
      setResponse("Survey submitted successfully!");

      // Reset form fields (optional: keep eventName locked)
      setName("");
      setEmail("");
      setContact("");
      setDestination("")
    } catch (err) {
      // Show server-side error message if available
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("Submission failed. Please try again.");
      }
    } finally {
      setLoading(false);

      // Auto-clear messages after 8 seconds
      setTimeout(() => {
        setError("");
        setResponse("");
      }, 6000);
    }
  };
  /* =========================
     Auto clear error
     ========================= */
  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(""), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const handleChange = (e) => {
    const value = e.target.value;
    setDestination(value);
    setShowError(false);
    if (value) {
      const filtered = countries.filter((country) =>
        country.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredCountries(filtered);
      setShowDropdown(true);
    } else {
      setFilteredCountries([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (country) => {
    setDestination(country);
    setFilteredCountries([]);
    setShowDropdown(false);
  };

  return (
    <div
      className="activation-container"
      style={{
        backgroundImage: `linear-gradient(90deg, rgba(2, 0, 36, 0.65) 0%, rgba(9, 9, 121, 0.4) 35%, rgba(0, 212, 255, 0) 100%), url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="card-wrapper">
        <header className="brand-header">
          <img src={require("../../img/logo.webp")} alt="logo" />
        </header>

        <form className="activation-form" onSubmit={submitForm}>
          {/* Event Section */}
          <section className="input-group">
            <label>Event Name | {dateFormat(date)} </label>
            <div className="input-with-action">
              <input
                type="text"
                value={eventName}
                placeholder="Enter event title..."
                disabled={eventLocked}
                onChange={(e) => setEventName(e.target.value)}
              />
              {!eventLocked && eventName && (
                <button type="button" className="btn-save" onClick={saveEvent}>
                  Lock
                </button>
              )}
              {eventLocked && (
                <button
                  type="button"
                  className="btn-reset"
                  onClick={resetEvent}
                >
                  Change
                </button>
              )}
            </div>
          </section>

          {/* User Details Section */}
          {eventLocked && (
            <section className="user-details animate-fade-in">
              <div className="section-divider">
                <span>Registration Details</span>
              </div>

              <div className="input-group">
                <input
                  type="text"
                  value={name}
                  placeholder="Full Name"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="input-group">
                <input
                  type="email"
                  value={email}
                  className={error.includes("email") ? "input-error" : ""}
                  placeholder="Email Address"
                  onChange={handleEmailChange}
                />
              </div>

              <div className="input-group">
                <input
                  type="tel"
                  value={contact}
                  placeholder="Phone Number"
                  onChange={(e) => setContact(e.target.value)}
                />
              </div>
              <div className="input-group">
                <div className="search-container">
                  <input
                    type="text"
                    value={destination}
                    onChange={handleChange}
                    placeholder="Select Nationality"
                    autoComplete="off"
                  />
                  {showDropdown && filteredCountries.length > 0 && (
                    <div className="country-dropdown">
                      <ul>
                        {filteredCountries.slice(0, 5).map((country, index) => (
                          <li key={index} onClick={() => handleSelect(country)}>
                            {country}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {showError && (
                  <div className="message-area">
                    <small className="msg-error">
                      Please select a nationality
                    </small>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Footer Actions */}
          <footer className="form-footer">
            {eventLocked && name && email && (
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? (
                  <span className="loader"> Please wait ... </span>
                ) : (
                  <span className="loader"> Complete Registration</span>
                )}
              </button>
            )}

            <div className="message-area">
              {response && <p className="msg-success">{response}</p>}
              {error && <p className="msg-error">{error}</p>}
            </div>
          </footer>
        </form>
      </div>
      {eventName && (
        <div>
          <Link to={`/select-winner/${encodeURIComponent(eventName)}`}>
            <button className="button">
              <span>Select Winners</span>
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Activations;
